import { useEffect, useMemo } from 'react'
import { enqueueSnackbar } from 'notistack'
import { useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { BreadcrumbSetter } from '@/components/Breadcrumbs'
import { DataCard } from '@/components/card'
import { OpenDialogButton } from '@/components/ConsoleDialog/handlers'
import {
  importAllEvents,
  ImportEventsForm
} from '@/components/forms/ImportEventsForm/ImportEventsForm'
import { TournamentDetailsTable } from '@/components/ImportEvents'
import { isImportedTournament, isImportedTournamentEvent } from '@/components/ImportEvents/utils'
import { STORE_STATUS } from '@/constants/store'
import { ROUTE_PARAM } from '@/router/constants'
import { useImportEventsTournamentStore } from '@/stores/importEventsTournament'

const IMPORT_ALL_BUTTON_LABEL = 'Import All'

export const TournamentDetailsView = () => {
  const tournamentId = useParams()[ROUTE_PARAM.TOURNAMENT_ID]
  const {
    currentTournament,
    data: events,
    fetchData,
    isLoading,
    resetState
  } = useImportEventsTournamentStore(
    useShallow((state) => ({
      isLoading: state.status === STORE_STATUS.loading,
      currentTournament: state.tournament,
      data: state.data,
      fetchData: state.fetchData,
      resetState: state.resetState
    }))
  )

  useEffect(() => {
    if (tournamentId) fetchData(tournamentId)
    return resetState
  }, [])

  const shouldShowImportAllButton = useMemo(() => {
    return events.length > 0 && events.some((event) => !isImportedTournamentEvent(event))
  }, [currentTournament])

  return (
    <>
      <BreadcrumbSetter
        param={ROUTE_PARAM.TOURNAMENT_ID}
        label={currentTournament ? currentTournament.name : ''}
        isLoading={isLoading}
      />
      <DataCard
        loading={isLoading}
        title={currentTournament ? `${currentTournament.name} - Import Events` : 'Import Events'}
        headerPostfixActions={
          currentTournament && shouldShowImportAllButton ? (
            <OpenDialogButton
              ButtonProps={{
                onClick: async () => {
                  if (isImportedTournament(currentTournament)) {
                    try {
                      await importAllEvents(currentTournament)

                      fetchData(currentTournament.id)

                      enqueueSnackbar('Successfully imported all events!', { variant: 'success' })
                    } catch (e) {
                      enqueueSnackbar(String(e), { variant: 'error' })
                      throw e
                    }
                  }
                }
              }}
              label={IMPORT_ALL_BUTTON_LABEL}
            >
              {!isImportedTournament(currentTournament) ? (
                <ImportEventsForm
                  multiple
                  tournament={currentTournament}
                  onSubmit={() => fetchData(currentTournament.id)}
                />
              ) : (
                <></>
              )}
            </OpenDialogButton>
          ) : null
        }
      >
        <TournamentDetailsTable />
      </DataCard>
    </>
  )
}
